/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AirmetFromBackend, SigmetFromBackend } from '@opengeoweb/api';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import {
  AlertBanner,
  dateUtils,
  useConfirmationDialog,
} from '@opengeoweb/shared';
import type {
  Airmet,
  AirmetFromFrontend,
  AviationProduct,
  CancelAirmet,
  CancelSigmet,
  ProductConfig,
  ProductStatus,
  Sigmet,
  SigmetConfig,
  SigmetFromFrontend,
} from '@opengeoweb/sigmet-airmet';
import {
  createAirmetPostParameter,
  createSigmetPostParameter,
  getConfirmationDialogButtonLabel,
  getConfirmationDialogTitle,
  isVAPhenomenon,
  prepareCancelSigmet,
  ProductAction,
} from '@opengeoweb/sigmet-airmet';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import {
  genericActions,
  uiActions,
  useUpdateSharedData,
  useRegisterFeatureSyncSource,
} from '@opengeoweb/store';
import type { syncGroupsTypes } from '@opengeoweb/store';
import type { ConfirmDialogProps } from './ConfirmWarningDialog';
import { ConfirmDeleteWarningDialog } from './ConfirmWarningDialog';
import { PublicWarningList } from './PublicWarningList';
import { useAutoRefetchOnExpiry, useWarnings } from '../../api/api-utils';
import {
  usePostAirmet,
  usePostSigmet,
  useProductConfig,
} from '../../api/aviation-api/hooks';
import {
  useMutateDeleteWarning,
  useMutateMaritimeWarningsEditor,
  useMutatePublicWarningEditor,
  usePostMaritimeWarnings,
  useSaveWarning,
} from '../../api/warning-api/hooks';
import type { WarningModuleStore } from '../../store/types';
import { warningsFiltersActions } from '../../store/warningsFilters/reducer';
import {
  getExpandedSectionsByPanelId,
  getWarningsFilterById,
} from '../../store/warningsFilters/selectors';
import type { ExpandedWarningSections } from '../../store/warningsFilters/types';
import type { FormAction } from '../../store/warningsForm/reducer';
import { warningsFormActions } from '../../store/warningsForm/reducer';
import {
  getPublicWarning,
  getSelectedPublicIsFormDirty,
  getSelectedWarningId,
  getWarningType,
} from '../../store/warningsForm/selectors';
import type {
  AirmetWarning,
  MaritimeWarning,
  MaritimeWarningsType,
  SigmetWarning,
  Warning,
  WarningType,
} from '../../store/warningsForm/types';
import {
  MaritimeWarningAction,
  PublicWarningStatus,
} from '../../store/warningsForm/types';
import { publicWarningDialogType } from '../../store/warningsForm/utils';
import { MAX_WARNINGS_DISPLAY } from '../../utils/constants';
import { useWarningsTranslation } from '../../utils/i18n';
import {
  amendAviationProduct,
  duplicateAviationProduct,
  renewAviationProduct,
} from '../../utils/sig-airmet-util';
import type {
  FilterState,
  PickedFilterState,
} from '../FilterList/filter-hooks';
import { useWarningFilters } from '../FilterList/filter-hooks';
import { TimeFilterMode } from '../FilterList/filter-utils';
import { warningLevelFeatureColors } from '../PublicWarningsForm';
import type { WarningLevel } from '../PublicWarningsForm/LevelField';
import { CancelAviationConfirmDialog } from '../PublicWarningsFormDialog/CancelAviationConfirmDialog';
import { warningFormConfirmationOptions } from '../PublicWarningsFormDialog/PublicWarningsFormDialogConnect';

export interface PublicWarningListConnectProps {
  panelId: string;
  presetFilterState?: PickedFilterState;
  presetExpandedSections?: ExpandedWarningSections;
}

export const PublicWarningListConnect: React.FC<
  PublicWarningListConnectProps
> = ({ panelId, presetExpandedSections, presetFilterState }) => {
  const updatePublicWarningEditor = useMutatePublicWarningEditor();
  const { mutateAsync: updateMaritimeWarningEditor } =
    useMutateMaritimeWarningsEditor();
  const { mutateAsync: deletePublicWarning } = useMutateDeleteWarning();
  const { mutateAsync: saveWarning } = useSaveWarning();
  const { mutateAsync: postMaritimeWarnings } = usePostMaritimeWarnings();
  const { t } = useWarningsTranslation();
  const dispatch = useDispatch();
  const confirmDialog = useConfirmationDialog();
  const { auth, isLoggedIn } = useAuthenticationContext();

  const savedFilterState = useSelector((store: WarningModuleStore) =>
    getWarningsFilterById(store, panelId),
  );

  const {
    combinedWarnings,
    refetchWarnings,
    isFetching,
    error,
    dataUpdatedAt,
  } = useWarnings(false, savedFilterState.time);

  useAutoRefetchOnExpiry(combinedWarnings, refetchWarnings);

  const lastUpdateTime = dateUtils.dateToString(
    new Date(dataUpdatedAt),
    dateUtils.DATE_FORMAT_HOURS,
  );
  const [confirmDialogOptions, setConfirmDialogOptions] = React.useState<
    ConfirmDialogProps | undefined
  >(undefined);
  const selectedWarningId = useSelector(getSelectedWarningId);
  const selectedFormWarning = useSelector(getPublicWarning) as
    | Warning
    | undefined;

  const isFormDirty = useSelector(getSelectedPublicIsFormDirty);

  const openPublicWarningDialog = async (
    formAction?: FormAction,
    warning?: Warning,
    warningType?: WarningType,
  ): Promise<void> => {
    if (isFormDirty) {
      const mayProceed = await confirmDialog(warningFormConfirmationOptions(t))
        .then(() => true)
        .catch(() => false);

      if (!mayProceed) {
        return;
      }
    }

    dispatch(
      warningsFormActions.openPublicWarningFormDialog({
        ...(warning && {
          warning,
        }),
        formState: formAction,
        warningType,
        panelId,
      }),
    );
  };

  const selectWarning = (warning?: Warning): void => {
    if (
      warning?.type === 'maritimeWarningCoastal' ||
      warning?.type === 'maritimeWarningNorthSea'
    ) {
      handleMaritimeProductClick(
        warning as MaritimeWarning,
        MaritimeWarningAction.SELECT,
      );
    } else {
      void openPublicWarningDialog('readonly', warning);
    }
  };
  const createWarning = (warningType?: WarningType): void => {
    void openPublicWarningDialog('', undefined, warningType);
  };

  const editWarning = async (publicWarning: Warning): Promise<void> => {
    if (publicWarning.id && auth?.username && !publicWarning.editor) {
      refetchWarnings();
      updatePublicWarningEditor.mutate({
        id: publicWarning.id,
        isEditor: true,
      });

      await openPublicWarningDialog('', {
        ...publicWarning,
        editor: auth?.username,
      });
    } else {
      void openPublicWarningDialog('', publicWarning);
    }
  };

  const expireWarning = (warning: Warning): void => {
    const expiredWarning: Warning = {
      ...warning,
      status: PublicWarningStatus.EXPIRED,
    };

    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-expire-confirm'),
      title: t('warning-dialog-expire-title'),
      description: t('warning-dialog-expire-description'),
      request: async () =>
        saveWarning({
          id: expiredWarning.id!,
          data: { ...expiredWarning, type: 'public' },
        }),
      callback: () =>
        onRemoveSuccess(expiredWarning.id!, t('warning-dialog-expire-succes')),
    });
  };

  const withdrawWarning = (warning: Warning): void => {
    const withdrawnWarning: Warning = {
      ...warning,
      status: PublicWarningStatus.WITHDRAWN,
    };

    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-withdraw-confirm'),
      title: t('warning-dialog-withdraw-title'),
      description: t('warning-dialog-withdraw-description'),
      request: async () =>
        saveWarning({
          id: withdrawnWarning.id!,
          data: { ...withdrawnWarning, type: 'public' },
        }),
      callback: () =>
        onRemoveSuccess(
          withdrawnWarning.id!,
          t('warning-dialog-withdraw-succes'),
        ),
    });
    void refetchWarnings();
  };

  const warningType = useSelector(getWarningType);

  const { data: aviationConfig } = useProductConfig(warningType, false);
  const { mutateAsync: postSigmet } = usePostSigmet();
  const { mutateAsync: postAirmet } = usePostAirmet();

  const postAviationProduct = async (
    productToPost: AviationProduct,
    newStatus: ProductStatus,
    type: 'sigmet' | 'airmet',
    opts?: { vaSigmetMoveToFIR?: string },
  ): Promise<SigmetFromBackend | AirmetFromBackend> => {
    const uuid: string | null = productToPost?.uuid ?? null;

    const body =
      newStatus === 'CANCELLED'
        ? getCancelSigmet(productToPost, type, opts?.vaSigmetMoveToFIR)
        : productToPost;

    if (type === 'sigmet') {
      const param: SigmetFromFrontend = createSigmetPostParameter(
        body as Sigmet | CancelSigmet,
        newStatus,
        uuid!,
      );
      return postSigmet(param);
    }
    const param: AirmetFromFrontend = createAirmetPostParameter(
      body as Airmet | CancelAirmet,
      newStatus,
      uuid!,
    );
    return postAirmet(param);
  };

  const getCancelSigmet = (
    selectedProduct: AviationProduct,
    type: 'sigmet' | 'airmet',
    vaSigmetMoveToFIR?: string,
  ): Sigmet | Airmet => {
    if (type === 'sigmet') {
      if (!vaSigmetMoveToFIR || vaSigmetMoveToFIR === 'UNKNOWN') {
        return selectedProduct as unknown as Sigmet;
      }
      return prepareCancelSigmet(
        selectedProduct as unknown as Sigmet,
        vaSigmetMoveToFIR,
      );
    }
    return selectedProduct as unknown as Airmet;
  };

  const cancelAviationProduct = async (
    warning: AviationProduct,
    type: 'sigmet' | 'airmet',
    opts?: { vaSigmetMoveToFIR?: string },
  ): Promise<SigmetFromBackend | AirmetFromBackend | null> => {
    const result = await postAviationProduct(warning, 'CANCELLED', type, opts);
    return result ?? null;
  };

  const [hiddenWarningIds, setHiddenWarningIds] = React.useState<Set<string>>(
    new Set(),
  );

  const handleToggleWarningVisibility = React.useCallback(
    (warningId: string): void => {
      setHiddenWarningIds((prev) => {
        const next = new Set(prev);
        if (next.has(warningId)) {
          next.delete(warningId);
        } else {
          next.add(warningId);
        }
        return next;
      });
    },
    [],
  );

  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [cancelCtx, setCancelCtx] = React.useState<{
    type: 'sigmet' | 'airmet';
    product: AviationProduct;
    showVA: boolean;
    sigmet?: Sigmet;
  } | null>(null);

  const openCancelDialog = (warning: SigmetWarning | AirmetWarning): void => {
    const type = warning.warningDetail.level === 'SIG' ? 'sigmet' : 'airmet';
    const product = warning.sigmetAirmetDetails as AviationProduct;
    const showVA = isVAPhenomenon(
      warning.sigmetAirmetDetails as unknown as Sigmet,
      ProductAction.CANCEL,
    );
    setCancelCtx({
      type,
      product,
      showVA,
      sigmet: warning.sigmetAirmetDetails as unknown as Sigmet,
    });
    setCancelOpen(true);
  };

  const closeCancelDialog = (): void => {
    setCancelOpen(false);
    setCancelCtx(null);
  };

  const handleAviationProductClick = (
    warning?: SigmetWarning | AirmetWarning,
    actionType?: ProductAction,
  ): void => {
    if (!warning) {
      return;
    }

    const type = warning.warningDetail.level === 'SIG' ? 'sigmet' : 'airmet';

    let updatedWarning: SigmetWarning | AirmetWarning | undefined;

    switch (actionType) {
      case ProductAction.AMEND:
        void openPublicWarningDialog(
          'amending',
          amendAviationProduct(warning, aviationConfig as ProductConfig),
          type,
        );
        break;
      case 'RENEW':
        updatedWarning = renewAviationProduct(
          warning,
          aviationConfig as ProductConfig,
        );
        void openPublicWarningDialog('edit', updatedWarning, type);
        break;
      case 'DUPLICATE':
        updatedWarning = duplicateAviationProduct(warning);
        void openPublicWarningDialog('edit', updatedWarning, type);
        break;
      case ProductAction.CANCEL: {
        openCancelDialog(warning);
        break;
      }
      case 'DISCARD': {
        const editor = warning.editor ?? '';
        const currentUser = auth?.username ?? '';

        const isLocked = editor !== '' && editor !== currentUser;

        setConfirmDialogOptions({
          confirmLabel: t('warning-dialog-delete-confirm'),
          title: t('warning-dialog-delete-title'),
          description: t('warning-dialog-delete-description'),
          request: async () =>
            postAviationProduct(
              warning.sigmetAirmetDetails as AviationProduct,
              'DISCARDED',
              type,
            ),
          callback: () => onRemoveSuccess(warning.id!),
          isLocked,
        });
        break;
      }

      default:
        break;
    }
  };

  const editMaritimeWarning = async (
    warning: MaritimeWarning,
  ): Promise<void> => {
    if (warning && auth?.username && !warning.editor) {
      try {
        await updateMaritimeWarningEditor({
          domain: warning.type,
          isEditor: true,
        });
      } catch {
        /* do nothing, refetching the list will update the form */
      }
      refetchWarnings();
    }
    void openPublicWarningDialog('', undefined, warning.type);
  };

  const extendMaritimeWarning = (warningToExtend: Warning): void => {
    const allMaritimeWarningsForType =
      (combinedWarnings?.filter(
        (warning) =>
          warning.type === warningToExtend.type &&
          warning.status === 'PUBLISHED',
      ) as MaritimeWarning[]) || ([] as MaritimeWarning[]);

    const now = Date.now();

    // Construct new warnings
    const extendedWarnings = allMaritimeWarningsForType.map((warning) => ({
      type: warning.type,
      status: PublicWarningStatus.PUBLISHED,
      editor: auth?.username ?? '',
      warningDetail: {
        phenomenon: 'wind',
        validFrom: warning.warningDetail.validFrom, // keep original validFrom when extending
        validUntil: new Date(now + 24 * 60 * 60 * 1000).toISOString(),
        level: warning.warningDetail.level,
        probability: 100,
        areas: warning.warningDetail.areas,
        windDirectionFrom: warning.warningDetail.windDirectionFrom,
        windForce: warning.warningDetail.windForce,
        changeType: warning.warningDetail.changeType,
        windDirectionTo: warning.warningDetail.windDirectionTo,
        descriptionOriginal: warning.warningDetail.descriptionOriginal,
        descriptionTranslation: warning.warningDetail.descriptionTranslation,
        domain: warning.type,
      },
    }));
    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-extend-confirm'),
      title: t('warning-dialog-extend-title'),
      description: t('warning-dialog-extend-description'),
      request: async () => {
        if (
          warningToExtend &&
          auth?.username &&
          warningToExtend.editor !== auth.username
        ) {
          await updateMaritimeWarningEditor({
            domain: warningToExtend.type as MaritimeWarningsType,
            isEditor: true,
          });
        }
        await postMaritimeWarnings(extendedWarnings);
      },
      callback: () =>
        onRemoveSuccess(warningToExtend.id!, t('warning-dialog-extend-succes')),
    });
    void refetchWarnings();
  };

  const handleMaritimeProductClick = (
    warning?: MaritimeWarning,
    actionType?: MaritimeWarningAction,
  ): void => {
    if (!warning) {
      return;
    }
    switch (actionType) {
      case MaritimeWarningAction.SELECT:
        void openPublicWarningDialog('', undefined, warning.type);
        break;
      case MaritimeWarningAction.UPDATE:
        void editMaritimeWarning(warning);
        break;
      case MaritimeWarningAction.EXTEND:
        extendMaritimeWarning(warning);
        break;
      default:
        break;
    }
  };

  const openSnackbar = (message: string): void => {
    dispatch(
      snackbarActions.openSnackbar({
        type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
        message,
      }),
    );
  };

  const deleteWarning = (warningId: string, warningEditor?: string): void => {
    const editor = warningEditor ?? '';
    const currentUser = auth?.username ?? '';

    const isLocked = editor !== '' && editor !== currentUser;

    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-delete-confirm'),
      title: t('warning-dialog-delete-title'),
      description: t('warning-dialog-delete-description'),
      request: async () =>
        deletePublicWarning(
          { id: warningId },
          { onSuccess: () => onRemoveSuccessHooks(warningId) },
        ),
      isLocked,
    });
  };

  const deleteReviewWarning = (warning: Warning): void => {
    const ignoredWarning: Warning = {
      ...warning,
      status: PublicWarningStatus.IGNORED,
    };

    setConfirmDialogOptions({
      confirmLabel: t('warning-dialog-delete-confirm'),
      title: t('warning-dialog-delete-title'),
      description: t('warning-dialog-delete-description'),
      request: async () =>
        saveWarning({
          id: ignoredWarning.id!,
          data: { ...ignoredWarning, type: 'public' },
        }),
      callback: () =>
        onRemoveSuccess(ignoredWarning.id!, t('warning-dialog-delete-succes')),
    });
    void refetchWarnings();
  };

  const onRemoveSuccessHooks = (warningId: string): void => {
    setConfirmDialogOptions(undefined);
    // Close warning dialog if needed
    if (selectedWarningId === warningId) {
      dispatch(
        uiActions.setToggleOpenDialog({
          setOpen: false,
          type: publicWarningDialogType,
        }),
      );
    }
  };

  // TODO remove when all is moved to tanstack
  const onRemoveSuccess = (
    warningId: string,
    snackbarMessage?: string,
  ): void => {
    // close confirm dialog
    setConfirmDialogOptions(undefined);
    // Close warning dialog if needed
    if (selectedWarningId === warningId) {
      dispatch(
        uiActions.setToggleOpenDialog({
          setOpen: false,
          type: publicWarningDialogType,
        }),
      );
    }

    if (snackbarMessage) {
      openSnackbar(snackbarMessage);
    }
    refetchWarnings();
  };

  const onUpdateFilters = React.useCallback(
    (filterState: Partial<FilterState>, systemUpdate = false): void => {
      dispatch(
        warningsFiltersActions.setWarningFilters({
          filterState,
          panelId,
          systemUpdate,
        }),
      );
    },
    [dispatch, panelId],
  );

  const filterHookState = useWarningFilters(
    combinedWarnings,
    onUpdateFilters,
    savedFilterState,
    presetFilterState,
  );

  const filteredWarnings = React.useMemo(
    () => combinedWarnings.filter(filterHookState.byFilterState),
    [combinedWarnings, filterHookState.byFilterState],
  );

  const isOverLimit = filteredWarnings.length > MAX_WARNINGS_DISPLAY;
  const hasCustomTimeFilter =
    savedFilterState.time.mode === TimeFilterMode.custom;
  const shouldShowAlertBanner = hasCustomTimeFilter && isOverLimit;

  const displayedWarnings = React.useMemo(
    () => (shouldShowAlertBanner ? [] : filteredWarnings),
    [shouldShowAlertBanner, filteredWarnings],
  );

  const expandedSections = useSelector((store: WarningModuleStore) =>
    getExpandedSectionsByPanelId(store, panelId),
  );

  const setExpandedSections = React.useCallback(
    (nextExpandedSections: Record<string, boolean>): void => {
      dispatch(
        warningsFiltersActions.setExpandedSectionsForPanel({
          panelId,
          expandedSections:
            nextExpandedSections as unknown as ExpandedWarningSections,
          systemUpdate: false,
        }),
      );
    },
    [dispatch, panelId],
  );

  React.useEffect(() => {
    if (!presetExpandedSections) {
      return;
    }

    dispatch(
      warningsFiltersActions.setExpandedSectionsForPanel({
        panelId,
        expandedSections: presetExpandedSections,
        systemUpdate: true,
      }),
    );
  }, [dispatch, panelId, presetExpandedSections]);

  const filteredWarningsByExpandedSections = React.useMemo(
    () =>
      displayedWarnings.filter((warning) => {
        const sectionKey = warning.status || warning.productStatus;
        return sectionKey ? Boolean(expandedSections[sectionKey]) : false;
      }),
    [displayedWarnings, expandedSections],
  );

  const selectedFormAreaIds = React.useMemo(
    () =>
      new Set(
        (selectedFormWarning?.warningDetail?.areas ?? [])
          .map((area) => area.areaId)
          .filter((areaId): areaId is string => Boolean(areaId)),
      ),
    [selectedFormWarning],
  );

  const allFeatures = React.useMemo(() => {
    return filteredWarningsByExpandedSections
      .flatMap((warning) => {
        const id = warning?.id;
        const warningIds = [warning?.id, warning?.warningDetail?.id].filter(
          (warningId): warningId is string => Boolean(warningId),
        );
        const hasMatchingAreaId = (warning?.warningDetail?.areas ?? []).some(
          (area) =>
            area.areaId !== undefined && selectedFormAreaIds.has(area.areaId),
        );

        if (
          !id ||
          warningIds.includes(selectedWarningId) ||
          hasMatchingAreaId ||
          hiddenWarningIds.has(id)
        ) {
          return [];
        }

        const areas = warning?.warningDetail?.areas ?? [];

        return areas
          .filter((area) => !!area.geoJSON)
          .map((area) => {
            const level = warning?.warningDetail?.level as
              | WarningLevel
              | undefined;
            const isAviationWarning =
              warning.warningDetail.level === 'SIG' ||
              warning.warningDetail.level === 'AIR';
            const styledGeoJSON = level
              ? warningLevelFeatureColors(
                  level as WarningLevel,
                  area.geoJSON!,
                  isAviationWarning,
                )
              : area.geoJSON!;

            return {
              id: id + (area.areaId ?? ''),
              originalId: id,
              geoJSON: styledGeoJSON,
            };
          });
      })
      .filter((feature) => !!feature.id);
  }, [
    filteredWarningsByExpandedSections,
    hiddenWarningIds,
    selectedFormAreaIds,
    selectedWarningId,
  ]);

  useRegisterFeatureSyncSource(panelId);

  const warningFeatureCollection = React.useMemo(
    () =>
      ({
        type: 'FeatureCollection',
        features: allFeatures.flatMap((feature) =>
          feature.geoJSON.features.map((geoFeature) => ({
            ...geoFeature,
            properties: {
              ...(geoFeature.properties ?? {}),
              originalId: feature.originalId,
            },
          })),
        ),
      }) as GeoJSON.FeatureCollection<
        GeoJSON.Geometry,
        syncGroupsTypes.PotentialDataProperties | null
      >,
    [allFeatures],
  );

  const warningLayerId = React.useMemo(
    () => `publicWarnings_${panelId}`,
    [panelId],
  );

  const warningFeatureLayersById = React.useMemo(
    () => ({
      [warningLayerId]: {
        layerId: warningLayerId,
        geoJSON: warningFeatureCollection,
      },
    }),
    [warningLayerId, warningFeatureCollection],
  );

  const { hoverId: hoveredFeatureId } = useUpdateSharedData(
    { featureLayersById: warningFeatureLayersById },
    panelId,
  );

  const handleHoverWarning = React.useCallback(
    (warningId?: string): void => {
      dispatch(
        genericActions.addFeatureSyncState({
          panelId,
          data: { hoverId: warningId },
        }),
      );
    },
    [dispatch, panelId],
  );

  return isLoggedIn ? (
    <>
      <PublicWarningList
        error={error ?? undefined}
        expandedSections={expandedSections}
        filterHookState={filterHookState}
        hiddenWarningIds={hiddenWarningIds}
        hoveredFeatureId={hoveredFeatureId}
        isLoading={isFetching}
        lastUpdatedTime={lastUpdateTime}
        limitAlertCount={filteredWarnings.length}
        onActionAviationWarning={handleAviationProductClick}
        onActionMaritimeWarning={handleMaritimeProductClick}
        onCreateWarning={createWarning}
        onDeleteReviewWarning={deleteReviewWarning}
        onDeleteWarning={deleteWarning}
        onEditWarning={editWarning}
        onExpireWarning={expireWarning}
        onHoverWarning={handleHoverWarning}
        onRefetchWarnings={refetchWarnings}
        onSelectWarning={selectWarning}
        onToggleWarningVisibility={handleToggleWarningVisibility}
        onWithdrawWarning={withdrawWarning}
        selectedWarningId={selectedWarningId}
        setExpandedSections={setExpandedSections}
        shouldDisableAviationAmendOption={
          aviationConfig?.disable_amend_action === true
        }
        showLimitAlert={shouldShowAlertBanner}
        warnings={displayedWarnings}
      />
      {confirmDialogOptions ? (
        <ConfirmDeleteWarningDialog
          callback={confirmDialogOptions.callback}
          confirmLabel={confirmDialogOptions.confirmLabel}
          description={confirmDialogOptions.description}
          isLocked={confirmDialogOptions.isLocked}
          onClose={(): void => setConfirmDialogOptions(undefined)}
          request={confirmDialogOptions.request}
          title={confirmDialogOptions.title}
        />
      ) : null}
      {!!cancelCtx && (
        <CancelAviationConfirmDialog
          aviationConfig={aviationConfig as SigmetConfig}
          cancelAviationProduct={async (product, t, opts) =>
            cancelAviationProduct(product, t, opts)
          }
          confirmLabel={getConfirmationDialogButtonLabel(
            t,
            ProductAction.CANCEL,
            cancelCtx.type,
          )}
          onClose={closeCancelDialog}
          open={cancelOpen}
          product={cancelCtx.product}
          showVA={cancelCtx.showVA}
          sigmet={cancelCtx.sigmet as Sigmet}
          title={getConfirmationDialogTitle(
            t,
            ProductAction.CANCEL,
            cancelCtx.type,
          )}
          type={cancelCtx.type}
        />
      )}
    </>
  ) : (
    <AlertBanner
      dataTestId="public-warning-list-auth-error"
      severity="error"
      title={t('error-auth-login')}
    />
  );
};
